import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './auth.css';
import { Field, PasswordField } from './AuthShell';
import { authService } from '../api/auth';
import { BUYER_NAV, ColorRail, LockIcon, TopBar } from '../../../commodity/components/caria';

/**
 * ForgotPassword — ported from
 * docs/html-prototypes/CAR-IA/car-ia-recuperar.html: the standard TopBar
 * chrome over a centred card, not the split pane that Login/Register use.
 *
 * The prototype walks four steps (correo → código → nueva contraseña → listo);
 * this screen keeps the app's existing two-step flow, because Cognito's
 * reset takes the code and the new password in a single call. Step 2 therefore
 * merges the prototype's s2 + s3.
 *
 * Behaviour unchanged:
 *   1. request: enter email → Cognito emails a reset code.
 *   2. reset:   enter code + new password → password updated → back to login.
 */
type Step = 'request' | 'reset';

const KeyIcon: React.FC = () => (
  <svg
    width="25"
    height="25"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="8" cy="15" r="4" />
    <path d="M10.85 12.15L19 4M18 5l2 2M15 8l2 2" />
  </svg>
);

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('request');

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const [apiError, setApiError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setNotice('');
    if (!emailValid) {
      setApiError('Escribe un correo válido.');
      return;
    }
    setLoading(true);
    try {
      await authService.forgotPassword({ email });
      setNotice(`Si existe una cuenta para ${email}, el código va en camino.`);
      setStep('reset');
    } catch (err: any) {
      setApiError(err.message || 'No pudimos iniciar el cambio de contraseña. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setNotice('');
    if (!/^\d{6}$/.test(code)) {
      setApiError('Escribe el código de 6 dígitos que te enviamos por correo.');
      return;
    }
    if (newPassword.length < 8) {
      setApiError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirm) {
      setApiError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword({ email, code, newPassword });
      navigate('/login', { state: { reset: true, email } });
    } catch (err: any) {
      setApiError(err.message || 'No pudimos cambiar la contraseña. Revisa el código e inténtalo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cia-recover">
      <TopBar nav={BUYER_NAV} />
      <ColorRail />

      <div className="cia-recover-wrap">
        <div className="cia-recover-box">
          <button className="cia-recover-back" onClick={() => navigate('/login')}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 6l-6 6 6 6" />
            </svg>
            Volver a ingresar
          </button>

          <span className="cia-recover-ic">
            {step === 'request' ? <KeyIcon /> : <LockIcon size={25} strokeWidth={1.8} />}
          </span>

          <h1>{step === 'request' ? 'Recuperar contraseña' : 'Nueva contraseña'}</h1>
          <p className="cia-auth-sub">
            {step === 'request'
              ? 'Escribe el correo de tu cuenta y te enviaremos un código de 6 dígitos para crear una nueva.'
              : 'Escribe el código que te enviamos y elige una contraseña de mínimo 8 caracteres.'}
          </p>

          {notice && <div className="cia-auth-alert cia-auth-alert--ok">{notice}</div>}
          {apiError && (
            <div className="cia-auth-alert cia-auth-alert--error" role="alert">
              {apiError}
            </div>
          )}

          {step === 'request' ? (
            <form onSubmit={handleRequest} noValidate>
              <Field
                id="email"
                testId="forgot-email"
                label="Correo"
                type="email"
                value={email}
                onChange={(v) => {
                  setEmail(v);
                  setApiError('');
                }}
                autoComplete="email"
              />
              <button
                type="submit"
                className="cia-auth-go"
                data-testid="forgot-request-submit"
                disabled={loading}
              >
                {loading ? (
                  <span className="cia-auth-spin" aria-label="Cargando" />
                ) : (
                  'Enviar código'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleReset} noValidate>
              <Field
                id="code"
                testId="forgot-code"
                label="Código de 6 dígitos"
                value={code}
                onChange={(v) => {
                  setCode(v.replace(/\D/g, '').slice(0, 6));
                  setApiError('');
                }}
                autoComplete="one-time-code"
                placeholder="000000"
              />
              <PasswordField
                id="newPassword"
                testId="forgot-newpass"
                label="Nueva contraseña"
                value={newPassword}
                onChange={(v) => {
                  setNewPassword(v);
                  setApiError('');
                }}
                autoComplete="new-password"
              />
              <PasswordField
                id="confirm"
                testId="forgot-confirm"
                label="Repite la contraseña"
                value={confirm}
                onChange={(v) => {
                  setConfirm(v);
                  setApiError('');
                }}
                autoComplete="new-password"
              />
              <button
                type="submit"
                className="cia-auth-go"
                data-testid="forgot-reset-submit"
                disabled={loading}
              >
                {loading ? (
                  <span className="cia-auth-spin" aria-label="Cargando" />
                ) : (
                  'Guardar contraseña'
                )}
              </button>
              <p className="cia-recover-hint">
                ¿No te llegó?{' '}
                <button type="button" onClick={() => setStep('request')}>
                  Reenviar código
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
